import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Package, MapPin, DollarSign, CheckCircle, XCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ClaimOrder = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState(null);
    const [alreadyClaimed, setAlreadyClaimed] = useState(false);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!isAuthenticated || authLoading) return;
            
            try {
                setLoading(true);
                const response = await ordersAPI.getOne(orderId);
                setOrder(response.data);
                
                // Check if already claimed
                if (response.data.claimed_by_vendeur_id && response.data.claimed_by_vendeur_id !== user?.id) {
                    setAlreadyClaimed(true);
                }
            } catch (err) {
                console.error('Error fetching order:', err);
                if (err.response?.status === 404) {
                    setError('Commande introuvable');
                } else if (err.response?.status === 403) {
                    setError('Accès non autorisé');
                } else {
                    setError('Erreur lors du chargement de la commande');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [orderId, isAuthenticated, authLoading, user?.id]);

    const handleClaim = async () => {
        try {
            setClaiming(true);
            await ordersAPI.claim(orderId);
            toast.success('🎉 Commande prise avec succès !');
            navigate('/commandes');
        } catch (err) {
            console.error('Error claiming order:', err);
            if (err.response?.data?.detail?.includes('already claimed')) {
                setAlreadyClaimed(true);
                toast.error('Cette commande a déjà été prise par un autre vendeur');
            } else {
                toast.error(err.response?.data?.detail || 'Erreur lors de la prise de commande');
            }
        } finally {
            setClaiming(false);
        }
    };

    // Redirect to login if not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl">🔐 Connexion Requise</CardTitle>
                        <CardDescription>
                            Connectez-vous pour prendre cette commande
                        </CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                        <Button 
                            onClick={() => navigate(`/connexion?redirect=/claim/${orderId}`)}
                            className="w-full"
                        >
                            Se Connecter
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Only vendors can claim orders
    if (!authLoading && user?.role !== 'vendeur') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <AlertTriangle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
                        <CardTitle className="text-xl">Réservé aux Vendeurs</CardTitle>
                        <CardDescription>
                            Seuls les vendeurs peuvent prendre des commandes via ce lien.
                        </CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                        <Button variant="outline" onClick={() => navigate('/commandes')}>
                            Aller aux Commandes
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
                    <p className="mt-4 text-muted-foreground">Chargement de la commande...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                        <CardTitle className="text-xl">{error}</CardTitle>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                        <Button variant="outline" onClick={() => navigate('/commandes')}>
                            Retour aux Commandes
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Order already claimed by this vendor
    if (order?.claimed_by_vendeur_id === user?.id || order?.assigned_vendeur_id === user?.id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                        <CardTitle className="text-xl text-green-700">Cette commande est à vous !</CardTitle>
                        <CardDescription>
                            Vous avez déjà pris cette commande. Vous pouvez la gérer depuis votre tableau de bord.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="p-4 bg-white rounded-lg border">
                            <p className="font-bold text-lg">{order.n_commande}</p>
                            <p className="text-muted-foreground">{order.produit}</p>
                            <p className="text-xl font-bold text-primary mt-2">{order.prix_total?.toLocaleString()} DA</p>
                        </div>
                    </CardContent>
                    <CardFooter className="flex justify-center">
                        <Button onClick={() => navigate('/commandes')} className="w-full">
                            Voir Mes Commandes
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Order already claimed by someone else
    if (alreadyClaimed) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                        <CardTitle className="text-xl text-red-700">Commande Déjà Prise</CardTitle>
                        <CardDescription>
                            Cette commande a déjà été prise. Soyez plus rapide la prochaine fois ! 🏃‍♂️
                        </CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                        <Button variant="outline" onClick={() => navigate('/commandes')}>
                            Retour aux Commandes
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Extract wilaya from address
    const wilaya = order?.adresse?.split(',').pop()?.trim() || 'N/A';

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg shadow-xl">
                <CardHeader className="text-center bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
                    <CardTitle className="text-2xl">🎯 Nouvelle Commande Disponible</CardTitle>
                    <CardDescription className="text-white/90">
                        Soyez le premier à prendre cette commande !
                    </CardDescription>
                </CardHeader>
                
                <CardContent className="p-6 space-y-4">
                    {/* Order Number */}
                    <div className="text-center">
                        <Badge className="text-lg px-4 py-2 bg-primary">
                            {order?.n_commande}
                        </Badge>
                    </div>

                    {/* Product Info */}
                    <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                        <div className="flex items-start gap-3">
                            <Package className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm text-muted-foreground">Produit</p>
                                <p className="font-semibold text-lg">{order?.produit}</p>
                            </div>
                        </div>

                        {/* Product URL - Link to actual website */}
                        {order?.produit_url && (
                            <a 
                                href={order.produit_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Voir le produit sur le site
                            </a>
                        )}

                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm text-muted-foreground">Wilaya</p>
                                <p className="font-semibold">{wilaya}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <DollarSign className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm text-muted-foreground">Prix Total</p>
                                <p className="font-bold text-2xl text-primary">{order?.prix_total?.toLocaleString()} DA</p>
                            </div>
                        </div>
                    </div>

                    {/* Client Info - MASKED */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-700 font-medium mb-1">
                            🔒 Informations Client Masquées
                        </p>
                        <p className="text-xs text-yellow-600">
                            Les coordonnées du client seront visibles après avoir pris la commande.
                        </p>
                    </div>

                    {/* Claim Button */}
                    <Button 
                        onClick={handleClaim}
                        disabled={claiming}
                        className="w-full h-14 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                    >
                        {claiming ? (
                            <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Prise en cours...
                            </>
                        ) : (
                            <>
                                🎯 PRENDRE CETTE COMMANDE
                            </>
                        )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                        En prenant cette commande, vous vous engagez à la traiter.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default ClaimOrder;
